import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { StatCard } from '../../atoms/StatCard'
import {
  ChartContainer,
  ChartGrid,
  ChartTitle,
  ErrorContainer,
} from '../../molecules/ChartComponents'

const ChartsRow = styled.div<{ $showTrends: boolean }>`
  display: grid;
  grid-template-columns: ${({ $showTrends }) =>
    $showTrends ? '1fr 2fr' : '1fr'};
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const ChartCard = styled(Box)`
  background: white;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`
import {
  processTransactionTypes,
  processTimeSeriesData,
  calculateKPIs,
  formatNumber,
  formatCurrency,
  getChartOptions,
} from './TransactionTypeChart.utils'
import {
  TransactionTypeChartProps,
  DEFAULT_TRANSACTION_TYPE_CHART_LABELS,
} from './TransactionTypeChart.types'

export interface TransactionTypeChartRef {
  exportChart: () => string | null
  exportAllCharts: () => (string | null)[]
}

export const TransactionTypeChart = forwardRef<
  TransactionTypeChartRef,
  TransactionTypeChartProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      showKPIs = true,
      showTrends = true,
      onTypeClick,
      formatTransactionType,
      labels = {},
    },
    ref
  ) => {
    const mergedLabels = { ...DEFAULT_TRANSACTION_TYPE_CHART_LABELS, ...labels }
    const distributionChartRef = useRef<HTMLDivElement>(null)
    const trendsChartRef = useRef<HTMLDivElement>(null)

    const distributionInstance = useRef<echarts.ECharts | null>(null)
    const trendsInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (distributionInstance.current) {
          return distributionInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        if (trendsInstance.current) {
          return trendsInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
      exportAllCharts: () => [
        distributionInstance.current?.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        }) ?? null,
        trendsInstance.current?.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
          excludeComponents: ['dataZoom'],
        }) ?? null,
      ],
    }))

    useEffect(() => {
      if (!loading && !error && data) {
        renderCharts()
      }
    }, [data, loading, error])

    // Separate cleanup effect that only runs on unmount
    useEffect(() => {
      return () => {
        distributionInstance.current?.dispose()
        trendsInstance.current?.dispose()
      }
    }, [])

    const renderCharts = () => {
      // Render distribution chart
      if (distributionChartRef.current) {
        const types = processTransactionTypes(data, formatTransactionType)

        if (types.length > 0) {
          // Dispose and recreate if needed
          if (distributionInstance.current) {
            distributionInstance.current.dispose()
            distributionInstance.current = null
          }
          distributionInstance.current = safeChartInit(
            distributionChartRef.current
          )
          const options = getChartOptions('donut', types, formatTransactionType)
          distributionInstance.current.setOption(options)

          if (onTypeClick) {
            distributionInstance.current.off('click') // Remove previous listeners
            distributionInstance.current.on('click', (params: any) => {
              const key = params.data?.key
              if (typeof key === 'string') {
                onTypeClick(key)
              }
            })
          }
        }
      }

      // Render trends chart
      if (trendsChartRef.current && showTrends) {
        const timeSeries = processTimeSeriesData(data)
        if (timeSeries.dates.length > 0) {
          // Dispose and recreate if needed
          if (trendsInstance.current) {
            trendsInstance.current.dispose()
            trendsInstance.current = null
          }
          trendsInstance.current = safeChartInit(trendsChartRef.current)
          const options = getChartOptions(
            'area',
            timeSeries,
            formatTransactionType
          )
          trendsInstance.current.setOption(options)
        }
      }
    }

    useEffect(() => {
      const handleResize = () => {
        distributionInstance.current?.resize()
        trendsInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    if (loading) {
      return (
        <div style={{ width: '100%' }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="large"
          >
            <Loading size="large" />
          </Box>
        </div>
      )
    }

    if (error) {
      return (
        <div style={{ width: '100%' }}>
          <ErrorContainer>
            <Text color="danger">{error}</Text>
          </ErrorContainer>
        </div>
      )
    }

    const kpis = calculateKPIs(data, formatTransactionType)

    return (
      <div style={{ width: '100%' }}>
        {showKPIs && (
          <ChartGrid columns={4}>
            <StatCard
              label={mergedLabels.totalTransactions!}
              value={formatNumber(kpis.totalTransactions)}
              variant="default"
            />
            <StatCard
              label={mergedLabels.dominantType!}
              value={kpis.dominantType}
              variant="primary"
            />
            <StatCard
              label={mergedLabels.totalVolume!}
              value={formatCurrency(kpis.totalVolume)}
              variant="default"
            />
            <StatCard
              label={mergedLabels.averageValue!}
              value={formatCurrency(kpis.avgTransactionValue)}
              variant="default"
            />
          </ChartGrid>
        )}

        <ChartsRow $showTrends={showTrends}>
          <ChartCard>
            <ChartTitle>{mergedLabels.distributionTitle}</ChartTitle>
            <ChartContainer>
              <div
                ref={distributionChartRef}
                style={{
                  width: '100%',
                  height: '320px',
                }}
              />
            </ChartContainer>
          </ChartCard>

          {showTrends && (
            <ChartCard>
              <ChartTitle>{mergedLabels.trendsTitle}</ChartTitle>
              <ChartContainer>
                <div
                  ref={trendsChartRef}
                  style={{
                    width: '100%',
                    height: '320px',
                  }}
                />
              </ChartContainer>
            </ChartCard>
          )}
        </ChartsRow>
      </div>
    )
  }
)

TransactionTypeChart.displayName = 'TransactionTypeChart'
